/**
 * Base Search Provider Interface
 * 
 * Defines the contract that all search providers must implement
 * to work with the UnifiedResultsModal system.
 */

export class BaseSearchProvider {
  /**
   * Get default search criteria for this provider
   * @returns {Object} Default criteria object
   */
  getDefaultCriteria() {
    throw new Error('getDefaultCriteria must be implemented by search provider');
  }

  /**
   * Get search controls configuration for the UI
   * @returns {Array} Array of control configurations
   */
  getSearchControls() {
    throw new Error('getSearchControls must be implemented by search provider');
  }

  /**
   * Perform the actual search
   * @param {Object} criteria - Search criteria from UI
   * @param {Object} data - Context data (casefileId, userId, etc.)
   * @returns {Promise<Array>} Array of search results
   */
  async search(criteria, data) {
    throw new Error('search must be implemented by search provider');
  }

  /**
   * Get available filters for results
   * @returns {Array} Array of filter configurations
   */
  getAvailableFilters() {
    return [];
  }

  /**
   * Apply a filter to results
   * @param {Array} results - Current results
   * @param {string} filterType - Type of filter to apply
   * @param {*} filterValue - Value to filter by
   * @returns {Array} Filtered results
   */
  applyFilter(results, filterType, filterValue) {
    return results;
  }

  /**
   * Sort results by given criteria
   * @param {Array} results - Results to sort
   * @param {string} sortBy - Sort criteria
   * @returns {Array} Sorted results
   */
  sortResults(results, sortBy) {
    switch (sortBy) {
      case 'date':
        return [...results].sort((a, b) => {
          const aDate = a.createdAt?.toDate?.() || new Date(0);
          const bDate = b.createdAt?.toDate?.() || new Date(0);
          return bDate - aDate;
        });
      
      case 'similarity':
        return [...results].sort((a, b) => 
          (b.similarityScore || 0) - (a.similarityScore || 0)
        );
      
      case 'provider':
        return [...results].sort((a, b) => 
          (a.provider || '').localeCompare(b.provider || '')
        );
      
      case 'relevance':
      default:
        // Default relevance sorting - evidence matches first, then by similarity/date
        return [...results].sort((a, b) => {
          // Evidence matches first
          if (a.matchType === 'evidence' && b.matchType === 'content') return -1;
          if (a.matchType === 'content' && b.matchType === 'evidence') return 1;
          
          // Then by similarity score
          if (a.similarityScore && b.similarityScore) {
            return b.similarityScore - a.similarityScore;
          }
          
          // Finally by date
          const aDate = a.createdAt?.toDate?.() || new Date(0);
          const bDate = b.createdAt?.toDate?.() || new Date(0);
          return bDate - aDate;
        });
    }
  }

  /**
   * Get statistics about the search results
   * @param {Array} results - Search results
   * @returns {Object} Statistics object
   */
  getStatistics(results) {
    return {
      'Total Results': results.length,
      'Evidence Matches': results.filter(r => r.matchType === 'evidence').length,
      'Content Matches': results.filter(r => r.matchType === 'content').length,
      'High Confidence': results.filter(r => (r.similarityScore || 0) > 0.7).length
    };
  }

  /**
   * Validate search criteria before performing search
   * @param {Object} criteria - Search criteria to validate
   * @param {Object} data - Context data
   * @returns {Object} { isValid: boolean, errors: Array }
   */
  validateCriteria(criteria, data) {
    return { isValid: true, errors: [] };
  }
}

export default BaseSearchProvider;
